import React, { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import API from "utils/helpers/APIHelper";

type Status = "loading" | "confirm" | "working" | "done" | "invalid" | "error";

const CORAL = "#F56948";
const BUTTON = "#C8421F";

const PageUnsubscribe: FC = () => {
  const { search } = useLocation();
  const token = new URLSearchParams(search).get("token") || "";

  const [status, setStatus] = useState<Status>(token ? "loading" : "invalid");
  const [email, setEmail] = useState("");

  // Looking at the page never unsubscribes anyone. It only asks the API
  // which (masked) email the link belongs to.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    (async () => {
      try {
        const data: any = await API.get("/api/v1/email_unsubscribe", { params: { token } });
        if (cancelled) return;
        setEmail(data?.email || "");
        setStatus("confirm");
      } catch (error: any) {
        if (cancelled) return;
        setStatus(error?.response?.status === 404 ? "invalid" : "error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const unsubscribe = async () => {
    setStatus("working");
    try {
      const data: any = await API.post("/api/v1/email_unsubscribe", { token });
      setEmail(data?.email || email);
      setStatus("done");
    } catch (error: any) {
      setStatus(error?.response?.status === 404 ? "invalid" : "error");
    }
  };

  const primaryButton =
    "block w-full text-center rounded-full px-5 py-3 text-base font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60";

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex flex-col items-center px-4 py-12">
      <Helmet>
        <title>Unsubscribe | Nomadroof</title>
        <meta name="robots" content="noindex" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>

      <a href="/" className="text-3xl font-bold mb-8" style={{ color: CORAL }}>
        Nomadroof
      </a>

      <main className="w-full max-w-md bg-white dark:bg-neutral-800 rounded-2xl shadow-lg px-8 py-10 text-center">
        {status === "loading" && (
          <div className="flex justify-center py-6">
            <div
              className="w-10 h-10 border-4 border-neutral-300 rounded-full animate-spin"
              style={{ borderTopColor: CORAL }}
            />
          </div>
        )}

        {(status === "confirm" || status === "working") && (
          <>
            <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-4">
              Unsubscribe from Nomadroof emails
            </h1>
            <p className="text-neutral-600 dark:text-neutral-300 mb-1">
              Confirm that you no longer want to receive news and offers from Nomadroof at
            </p>
            <p className="font-semibold text-neutral-900 dark:text-white mb-5">{email}</p>
            <p className="text-sm text-neutral-600 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 rounded-lg px-3 py-3 mb-6">
              We will still send you important messages about your bookings and your account.
            </p>
            <button
              type="button"
              onClick={unsubscribe}
              disabled={status === "working"}
              className={primaryButton}
              style={{ backgroundColor: BUTTON }}
            >
              {status === "working" ? "Unsubscribing…" : "Confirm unsubscribe"}
            </button>
            <a
              href="/"
              className="inline-block mt-4 text-sm font-medium"
              style={{ color: BUTTON }}
            >
              Cancel
            </a>
          </>
        )}

        {status === "done" && (
          <>
            <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-4">
              You've been unsubscribed
            </h1>
            <p className="text-neutral-600 dark:text-neutral-300 mb-2">
              We've removed{" "}
              <strong className="text-neutral-900 dark:text-white">{email}</strong> from our
              marketing list. You won't receive news or offers from Nomadroof anymore.
            </p>
            <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-6">
              You will still receive important messages about your bookings and account.
            </p>
            <a
              href="/"
              className="inline-block rounded-full border border-neutral-300 dark:border-neutral-600 px-6 py-3 text-base font-medium text-neutral-900 dark:text-white"
            >
              Back to nomadroof.com
            </a>
          </>
        )}

        {status === "invalid" && (
          <>
            <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-4">
              This link is not valid
            </h1>
            <p className="text-neutral-600 dark:text-neutral-300 mb-6">
              The unsubscribe link may be incomplete. Please open the link from the email again,
              or contact us and we will remove you.
            </p>
            <a
              href="/contact"
              className="inline-block rounded-full border border-neutral-300 dark:border-neutral-600 px-6 py-3 text-base font-medium text-neutral-900 dark:text-white"
            >
              Contact us
            </a>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-4">
              Something went wrong
            </h1>
            <p className="text-neutral-600 dark:text-neutral-300 mb-6">
              We could not reach the server. Please check your connection and try again.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={primaryButton}
              style={{ backgroundColor: BUTTON }}
            >
              Try again
            </button>
          </>
        )}
      </main>

      <p className="mt-8 text-xs text-neutral-500 dark:text-neutral-400">
        Nomadroof · Student housing in Lima
      </p>
    </div>
  );
};

export default PageUnsubscribe;